import { gql, useMutation } from '@apollo/client';

/* ============================================
   GRAPHQL MUTATION DEFINITIONS
   ============================================ */

// Create driver.
const CREATE_DRIVER = gql`
  mutation CreateDriver(
    $email: EmailAddress!
    $password: String!
    $name: String!
    $phoneNumber: String!
  ) {
    createDriver(
      email: $email
      password: $password
      name: $name
      phoneNumber: $phoneNumber
    ) {
      id
      email
      name
      phoneNumber
      licensePlate
      carMake
      carModel
      carColor
      isOnDuty
      currentRideId
      riderQueue
      driverNextAvailableAt
    }
  }
`;

// Update driver.
const UPDATE_DRIVER = gql`
  mutation UpdateDriver(
    $id: ID!
    $email: EmailAddress
    $name: String
    $phoneNumber: String
    $licensePlate: String
    $carMake: String
    $carModel: String
    $carColor: String
    $isOnDuty: Boolean
    $currentRideId: ID
    $riderQueue: Int
    $driverNextAvailableAt: Long
    $profilePicture: String
    $seats: Int
  ) {
    updateDriver(
      id: $id
      email: $email
      name: $name
      phoneNumber: $phoneNumber
      licensePlate: $licensePlate
      carMake: $carMake
      carModel: $carModel
      carColor: $carColor
      isOnDuty: $isOnDuty
      currentRideId: $currentRideId
      riderQueue: $riderQueue
      driverNextAvailableAt: $driverNextAvailableAt
      profilePicture: $profilePicture
      seats: $seats
    ) {
      id
      email
      name
      phoneNumber
      licensePlate
      carMake
      carModel
      carColor
      isOnDuty
      currentRideId
      riderQueue
      driverNextAvailableAt
      profilePicture
      seats
      createdAt
      updatedAt
    }
  }
`;

// Delete driver.
const DELETE_DRIVER = gql`
  mutation DeleteDriver($id: ID!) {
    deleteDriver(id: $id) {
      message
    }
  }
`;

// Update driver's duty status.
const UPDATE_DRIVER_IS_ON_DUTY = gql`
  mutation UpdateDriverDutyStatus($id: ID!, $isOnDuty: Boolean!) {
    updateDriverIsOnDuty(id: $id, isOnDuty: $isOnDuty) {
      isOnDuty
    }
  }
`;

// Update driver's next available time.
const UPDATE_DRIVER_NEXT_AVAILABLE_AT = gql`
  mutation UpdateDriverNextAvailableAt(
    $id: ID!
    $riderQueueChange: Int!
    $secondsToAdd: Int!
  ) {
    updateDriverNextAvailableAt(
      id: $id
      riderQueueChange: $riderQueueChange
      secondsToAdd: $secondsToAdd
    ) {
      riderQueue
      driverNextAvailableAt
    }
  }
`;

// Update driver location.
const UPDATE_DRIVER_LOCATION = gql`
  mutation UpdateDriverLocation(
    $id: ID!
    $latitude: Float!
    $longitude: Float!
  ) {
    updateDriverLocation(id: $id, latitude: $latitude, longitude: $longitude) {
      id
      latitude
      longitude
    }
  }
`;

/* ============================================
   MUTATION HOOKS
   ============================================ */

// Create a new driver.
export const useCreateDriver = () => {
  const [
    createDriver,
    { loading: loadingCreateDriver, error: errorCreateDriver },
  ] = useMutation(CREATE_DRIVER, {
    onError: () => {
      // TODO: Remove in production.
      // console.log('Create Driver Error.');
    },
    onCompleted: (data) => {
      // TODO: Remove in production.
      // console.log('Driver Created Successfully.');
      // console.log(`Return data: `, data.createDriver);
    },
  });

  return { createDriver, loadingCreateDriver, errorCreateDriver };
};

// Update an exisiting driver.
export const useUpdateDriver = () => {
  const [
    updateDriver,
    { loading: loadingUpdateDriver, error: errorUpdateDriver },
  ] = useMutation(UPDATE_DRIVER, {
    onError: () => {
      // TODO: Remove in production.
      // console.log('Update Driver Error.');
    },
    onCompleted: (data) => {
      // TODO: Remove in production.
      // console.log('Driver Updated Successfully.');
      // console.log(`Return data: `, data.updateDriver);
    },
  });

  return { updateDriver, loadingUpdateDriver, errorUpdateDriver };
};

// Delete an exisiting driver.
export const useDeleteDriver = () => {
  const [
    deleteDriver,
    { loading: loadingDeleteDriver, error: errorDeleteDriver },
  ] = useMutation(DELETE_DRIVER, {
    onError: () => {
      // TODO: Remove in production.
      // console.log('Delete Driver Error.');
    },
    onCompleted: (data) => {
      // TODO: Remove in production.
      // console.log('Driver Deleted Successfully.');
      // console.log(`Return data: `, data.deleteDriver);
    },
  });

  return { deleteDriver, loadingDeleteDriver, errorDeleteDriver };
};

// Update driver's duty status.
export const useUpdateDriverDutyStatus = () => {
  const [
    updateDriverDutyStatus,
    {
      loading: loadingUpdateDriverDutyStatus,
      error: errorUpdateDriverDutyStatus,
    },
  ] = useMutation(UPDATE_DRIVER_IS_ON_DUTY, {
    onError: () => {
      // TODO: Remove in production.
      // console.log('Update Driver Duty Status Error:', error);
      // console.log('Variables:', error.graphQLErrors[0]?.extensions?.variables);
    },
    onCompleted: (data) => {
      // TODO: Remove in production.
      // console.log('Driver Duty Status Updated Successfully.');
      // console.log(`Return data: `, data.updateDriverIsOnDuty);
    },
  });

  //TODO: Remove in production.
  const wrappedUpdateDriverDutyStatus = (options) => {
    // TODO: Delete console logs.
    // console.log('Mutation variables for DUTY STATUS:', options.variables);
    return updateDriverDutyStatus(options);
  };

  return {
    updateDriverDutyStatus: wrappedUpdateDriverDutyStatus,
    loadingUpdateDriverDutyStatus,
    errorUpdateDriverDutyStatus,
  };
};

// Update driver's next availability.
export const useUpdateDriverNextAvailableAt = () => {
  const [
    updateDriverNextAvailableAt,
    {
      loading: loadingUpdateDriverNextAvailableAt,
      error: errorUpdateDriverNextAvailableAt,
    },
  ] = useMutation(UPDATE_DRIVER_NEXT_AVAILABLE_AT, {
    onError: () => {
      // TODO: Remove in production.
      // console.log('Update Driver Next Available Error.');
    },
    onCompleted: (data) => {
      // TODO: Remove in production.
      // console.log('Driver Next Available Updated Successfully.');
      // console.log(`Return data: `, data.updateDriverNextAvailableAt);
    },
  });

  //TODO: Remove in production.
  const wrappedupdateDriverNextAvailableAt = (options) => {
    // TODO: Delete console logs.
    // console.log(
    //   'Mutation variables for DRIVER AVAILABILITY:',
    //   options.variables
    // );
    return updateDriverNextAvailableAt(options);
  };

  return {
    updateDriverNextAvailableAt: wrappedupdateDriverNextAvailableAt,
    loadingUpdateDriverNextAvailableAt,
    errorUpdateDriverNextAvailableAt,
  };
};

// Update driver's location.
export const useUpdateDriverLocation = () => {
  const [updateDriverLocation] = useMutation(UPDATE_DRIVER_LOCATION, {
    onError: () => {},
  });

  return { updateDriverLocation };
};
